import { api } from "./axios";

export type GroupByInterval = "hour" | "day" | "week" | "month";

export interface BodyTemperatureDataPoint {
  value: number;
  date: string;
}

export interface BodyTemperatureMetadata {
  min: number;
  max: number;
  avg: number;
}

export interface BodyTemperatureResponse {
  data: BodyTemperatureDataPoint[];
  metadata: BodyTemperatureMetadata;
}

export interface BodyTemperatureParams {
  patient_id: number;
  group_by_interval?: GroupByInterval;
  start_date?: string;
  stop_date?: string;
}

export const getBodyTemperatureData = async (
  params: BodyTemperatureParams,
): Promise<BodyTemperatureResponse> => {
  const response = await api.get<BodyTemperatureResponse>(
    "/measurements/body-temperature",
    {
      params,
    },
  );
  return response.data;
};

// Helper function to determine grouping interval based on time range
export const getGroupByInterval = (timeRange: string): GroupByInterval => {
  switch (timeRange) {
    case "1d":
      return "hour";
    case "7d":
      return "day";
    case "1m":
      return "day";
    case "6m":
      return "month";
    case "1y":
      return "month";
    default:
      return "day";
  }
};

// Helper function to calculate start and end dates for each time range
export const getDateRange = (
  timeRange: string,
  navigationOffset = 0,
  selectedDate?: Date,
): { start_date: string; stop_date: string } => {
  let baseDate: Date;

  if (selectedDate) {
    // Use the selected date directly
    baseDate = new Date(selectedDate);
  } else {
    // Use current date + navigation offset
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();

    baseDate = new Date(currentYear, currentMonth, currentDate);
    baseDate.setDate(baseDate.getDate() + navigationOffset);
  }

  const offsetYear = baseDate.getFullYear();
  const offsetMonth = baseDate.getMonth();
  const offsetDate = baseDate.getDate();

  // Helper function to convert local date to YYYY-MM-DD string
  const toLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  };

  // Helper function to convert date to UTC YYYY-MM-DD string
  const toUTCDateString = (date: Date): string => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  };

  switch (timeRange) {
    case "1d": {
      // Create local day boundaries (00:00 to 23:59)
      const localStart = new Date(offsetYear, offsetMonth, offsetDate, 0, 0, 0);
      const localEnd = new Date(
        offsetYear,
        offsetMonth,
        offsetDate,
        23,
        59,
        59,
      );

      return {
        start_date: toUTCDateString(localStart),
        stop_date: toUTCDateString(localEnd),
      };
    }
    case "7d": {
      // Current week (Monday 00:00 to next Monday 00:00)
      const start = new Date(
        offsetYear,
        offsetMonth,
        offsetDate - baseDate.getDay() + 1,
      ); // Monday
      start.setHours(0, 0, 0, 0);
      const end = new Date(
        offsetYear,
        offsetMonth,
        offsetDate - baseDate.getDay() + 8,
      ); // Next Monday
      end.setHours(0, 0, 0, 0);

      return {
        start_date: toLocalDateString(start),
        stop_date: toLocalDateString(end),
      };
    }
    case "1m": {
      // Current month (1st 00:00 to 1st of next month 00:00)
      const start = new Date(offsetYear, offsetMonth, 1);
      const end = new Date(offsetYear, offsetMonth + 1, 1);
      return {
        start_date: toLocalDateString(start),
        stop_date: toLocalDateString(end),
      };
    }
    case "6m": {
      // Current 6-month period (start of period to start of next period)
      const isFirstHalf = offsetMonth < 6;
      const start = new Date(offsetYear, isFirstHalf ? 0 : 6, 1); // Jan 1 or Jul 1
      const end = new Date(offsetYear, isFirstHalf ? 6 : 12, 1); // Jul 1 or Jan 1 next year
      return {
        start_date: toLocalDateString(start),
        stop_date: toLocalDateString(end),
      };
    }
    case "1y": {
      // Current year (Jan 1 00:00 to Jan 1 next year 00:00)
      const start = new Date(offsetYear, 0, 1);
      const end = new Date(offsetYear + 1, 0, 1);
      return {
        start_date: toLocalDateString(start),
        stop_date: toLocalDateString(end),
      };
    }
    default:
      return {
        start_date: toLocalDateString(
          new Date(offsetYear, offsetMonth, offsetDate),
        ),
        stop_date: toLocalDateString(
          new Date(offsetYear, offsetMonth, offsetDate + 1),
        ),
      };
  }
};
